import * as t from 'io-ts';
import { pipe } from 'fp-ts/function';
import * as E from 'fp-ts/Either';
import * as A from 'fp-ts/Array';
import * as S from 'fp-ts/string';

import { Store } from '../../services/store';
import { useVotesCastQuery } from '../../generated/graphql';
import { useAuth } from '../../contexts/AuthContext';
import { useStore } from '../../contexts/StoreContext';
import { isNil } from '../../utils/isNil';
import { wait } from '../../utils/wait';

const getVoteCastKey = (matchId: string) => `lv.${matchId}`;

const votesCastHistoryDecoder = t.array(t.string);

export const getExistingVotesCast =
  (store: Pick<Store, 'get' | 'set'>) => (matchId: string) =>
    pipe(
      getVoteCastKey(matchId),
      (v) => store.get(v),
      votesCastHistoryDecoder.decode,
      E.getOrElse((): string[] => []),
    );

/**
 * Set the votes cast in the local store
 * Used to keep server-side state in sync with client side
 *
 * @param store
 */
export const storeVotesCast =
  (store: Pick<Store, 'get' | 'set'>) =>
  (matchId: string, voteTypes: string[]) => {
    const key = getVoteCastKey(matchId);

    pipe(voteTypes, A.uniq(S.Eq), (vts) => store.set(key, vts));
  };

export const storeVoteCast =
  (store: Pick<Store, 'get' | 'set'>) =>
  (matchId: string, voteType: string) => {
    const key = getVoteCastKey(matchId);
    pipe(
      store.get(key),
      votesCastHistoryDecoder.decode,
      E.map(A.concat([voteType])),
      E.map(A.uniq(S.Eq)),
      E.fold(
        () => store.set(key, [voteType]),
        (res) => store.set(key, res),
      ),
    );
  };

/**
 * Get vote casting history for a match. If user is logged in will use
 * values from the server. Otherwise will use values persisted to localstorage.
 *
 * @param matchId
 */
export const useAlreadyCastVotes = (matchId: string) => {
  const { user } = useAuth();
  const isLoggedIn = !isNil(user);
  const store = useStore();

  const [votesCastResponse] = useVotesCastQuery({
    variables: { matchId },
    requestPolicy: 'cache-and-network',
  });

  const votesCast = votesCastResponse.data?.match?.voteTypesCast;

  const setVoteCast = (voteType: string) => {
    // only save the cast vote in localstorage if the user is logged out
    // as we save voting history server side for signed in users.
    if (!isLoggedIn) {
      // do it async so it doesn't race any rerenders that trigger redirects
      wait(1).then(() => storeVoteCast(store)(matchId, voteType));
    }
  };

  const alreadyCastVotes = isLoggedIn
    ? votesCast || []
    : getExistingVotesCast(store)(matchId);

  return {
    alreadyCastVotes,
    setVoteCast,
    fetching: isLoggedIn ? votesCastResponse.fetching : false,
  };
};
